import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import WebsitePreview from '../components/WebsitePreview';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const location = useLocation();

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'real-estate', name: 'Real Estate' },
    { id: 'events', name: 'Events' },
    { id: 'sports', name: 'Sports' },
    { id: 'commercial', name: 'Commercial' },
  ];

  const projects = [
    {
      id: 0,
      title: 'Discover Lehigh Valley 2025 Commercial',
      category: 'commercial',
      thumbnail: '/images/Fall - Lone Lane - 9.jpg',
      video: '/videos/scenery website quick vid 1.mp4',
      isImage: true,
      description: "Official drone cinematography for the Discover Lehigh Valley 2025 tourism campaign, showcasing the region's natural beauty, vibrant communities, and attractions through stunning aerial perspectives.",
      client: 'Discover Lehigh Valley Tourism Board',
      location: 'Lehigh Valley, PA',
      deliverables: 'Promotional Video, Social Media Content, Tourism Campaign',
      externalUrl: 'https://www.discoverlehighvalley.com/'
    },
    {
      id: 9,
      title: 'PA Gets It Done Campaign',
      category: 'commercial',
      thumbnail: '/images/Fall - Earl Adams - 1.jpg',
      video: '/videos/scenery website quick vid 1.mp4',
      isImage: true,
      description: "Official drone cinematography for Pennsylvania's statewide economic development campaign, showcasing the Commonwealth's innovative industries and business-friendly environment.",
      client: 'Pennsylvania Commonwealth',
      location: 'Statewide, PA',
      deliverables: 'Promotional Video, Aerial Footage, Economic Development Campaign',
      externalUrl: 'https://www.jtwo.tv/portfolio/pa-gets-it-done-2/'
    },
    {
      id: 1,
      title: 'Skirmish Paintball: Invasion of Normandy',
      category: 'events',
      thumbnail: '/images/Skirmish paintball photo website.jpg',
      video: '/videos/Paintball quick website vid.mp4',
      isImage: false,
      description: "Dynamic FPV and aerial coverage of the world-renowned Invasion of Normandy scenario event at Skirmish Paintball, capturing the intensity and scale of this massive paintball battle.",
      client: 'Skirmish Paintball',
      location: 'Jim Thorpe, PA (Skirmish Paintball Fields)',
      deliverables: 'Event Highlight Reel, Promotional Footage, Social Media Content'
    },
    {
      id: 2,
      title: 'Lehigh Valley Phantoms - Media Day',
      category: 'sports',
      thumbnail: '/images/Phantoms TB toss.jpg',
      video: '/videos/Phantoms website video.mp4',
      isImage: false,
      description: 'Dynamic FPV drone coverage of the Lehigh Valley Phantoms Media Day, capturing professional hockey action and fan excitement at the PPL Center.',
      client: 'Lehigh Valley Phantoms',
      location: 'PPL Center - Allentown, PA',
      deliverables: 'Live Event Coverage, Promotional Highlight Reel, Social Media Content'
    },
    {
      id: 4,
      title: 'Indoor FPV Construction',
      category: 'commercial',
      thumbnail: '/images/MLB.jpg',
      video: '/videos/Construction website indoor fpv.mp4',
      isImage: false,
      description: 'Immersive FPV drone footage capturing interior spaces of construction projects with dynamic movement and impossible transitions.',
      client: 'Home Depot',
      location: 'Allentown, PA',
      deliverables: 'Interior FPV Footage, Progress Documentation, Marketing Content'
    },
    {
      id: 5,
      title: 'Paving & Infrastructure',
      category: 'commercial',
      thumbnail: '/images/Paving still from video.JPG',
      video: '/videos/Paving website video.mp4',
      isImage: false,
      description: 'Aerial documentation of infrastructure projects, highlighting progress and providing valuable oversight of large-scale paving operations.',
      client: 'Richard Diehl Paving & Sons',
      location: 'Easton, PA',
      deliverables: 'Monthly Progress Reports, 3D Mapping, Marketing Assets'
    },
    {
      id: 6,
      title: 'Drift Coverage',
      category: 'sports',
      thumbnail: '/images/vinyard+x.jpg',
      video: '/videos/Drift video for website.mp4',
      isImage: false,
      description: 'High-octane drone coverage of drift racing events, capturing the speed, smoke, and excitement with dynamic FPV perspectives.',
      client: 'East Coast Drift Series',
      location: 'Evergreen Raceway, PA',
      deliverables: 'Event Highlight Videos, Social Media Content, Promotional Material'
    },
    {
      id: 7,
      title: 'Lehigh Valley Velodrome',
      category: 'sports',
      thumbnail: '/images/pat hit disc - frame at 0m7s.jpg',
      video: '/videos/Velodrome Cycle Website vid.mp4',
      isImage: false,
      description: 'Professional coverage of cycling events at the velodrome, utilizing dynamic drone movements to follow the action.',
      client: 'Valley Preferred Cycling Center',
      location: 'Trexlertown, PA',
      deliverables: 'Event Highlights, Athlete Profiles, Training Footage'
    },
    {
      id: 8,
      title: 'Wedding Cinematography',
      category: 'events',
      thumbnail: '/images/Wedding.jpg',
      video: '/videos/weddings website video.mp4',
      isImage: false,
      description: 'Breathtaking aerial footage capturing the beauty and emotion of wedding celebrations, from intimate ceremonies to grand receptions.',
      client: 'Various Wedding Clients',
      location: 'Lehigh Valley, PA',
      deliverables: 'Wedding Highlight Films, Aerial Venue Showcases, Social Media Content'
    }
  ];

  const filteredProjects = activeCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeCategory);

  const openLightbox = (project) => {
    setCurrentProject(project);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const openExternalVideo = (url) => {
    window.open(url || 'https://www.discoverlehighvalley.com/', '_blank');
  };

  // Check if we came from homepage with a specific project to show
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const projectId = searchParams.get('project');
    
    if (projectId) {
      const project = projects.find(p => p.id.toString() === projectId);
      if (project) {
        openLightbox(project);
      }
    }
  }, [location]);

  return (
    <div className="portfolio-page pt-24 pb-16">
      <SEO 
        title="Lehigh Valley Drone Portfolio | Cosmik Drones | Aerial & FPV Work"
        description="Explore Cosmik Drones' portfolio: FAA-certified aerial photography & FPV video in Lehigh Valley, PA. Featuring commercial, event & sports projects. Lehigh Valley Chamber Member."
        canonicalUrl="https://cosmikdrones.com/portfolio"
      />
      <SchemaMarkup 
        type="CollectionPage"
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Cosmik Drones Portfolio - Lehigh Valley Aerial Photography & Videography",
          "description": "A collection of professional drone videography and photography projects by Cosmik Drones, showcasing work in real estate, events, sports, and commercial sectors in Lehigh Valley, Pennsylvania.",
          "url": "https://cosmikdrones.com/portfolio",
          "publisher": {
            "@type": "Organization",
            "name": "Cosmik Drones",
            "logo": {
              "@type": "ImageObject",
              "url": "https://cosmikdrones.com/Cosmik%20Logo/Kolonia_Logo.png"
            }
          },
          "hasPart": projects.map(project => ({
            "@type": project.isImage ? "ImageObject" : "VideoObject",
            "name": project.title,
            "description": project.description,
            "thumbnailUrl": `https://cosmikdrones.com${project.thumbnail}`,
            ...(project.isImage ? { "contentUrl": `https://cosmikdrones.com${project.thumbnail}` } : { "contentUrl": `https://cosmikdrones.com${project.video}`, "uploadDate": project.date || new Date().toISOString().split('T')[0] }), // Assuming project.date exists or fallback
            "author": {
              "@type": "Organization",
              "name": "Cosmik Drones"
            },
            "spatialCoverage": {
                "@type": "Place",
                "name": project.location
            }
          }))
          // Add more details to hasPart if available, e.g., dateCreated for images, uploadDate for videos
        }}
      />
      
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-lightText mb-6">My Portfolio</h1>
          <p className="text-xl text-lightText/80 max-w-4xl mx-auto">
            Explore my collection of aerial cinematography and photography projects, showcasing my work across various industries and locations.
          </p>
        </motion.div>

        {/* Featured Project - Discover Lehigh Valley */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-24"
          id="discover-lehigh-valley"
        >
          <div className="glass-card p-8 rounded-lg overflow-hidden">
            <div className="flex flex-col lg:flex-row gap-8 items-center">
              <div className="lg:w-7/12">
                <div className="relative rounded-lg overflow-hidden cursor-pointer group" onClick={() => openExternalVideo(projects[0].externalUrl)}>
                  <WebsitePreview 
                    site="discover-lehigh-valley"
                    onClick={() => openExternalVideo(projects[0].externalUrl)}
                    delay={0.1}
                  />
                  <div className="absolute top-4 right-4 bg-lightText/20 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-lightText text-sm font-medium">Featured Project</span>
                  </div>
                </div>
              </div>
              <div className="lg:w-5/12">
                <div className="bg-primary/10 px-4 py-2 inline-block rounded-md mb-4">
                  <span className="text-primary font-medium">Official Tourism Campaign</span>
                </div>
                <h2 className="text-3xl font-bold text-lightText mb-4">Discover Lehigh Valley 2025 Commercial</h2>
                <p className="text-lg text-lightText/80 mb-6">
                  Proud to have created the official drone cinematography for the Discover Lehigh Valley 2025 tourism campaign. The video showcases the region's natural beauty, vibrant communities, and attractions through stunning aerial perspectives.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="bg-primary/20 p-2 rounded-full mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lightText font-bold">Client</h3>
                      <p className="text-lightText/80">Discover Lehigh Valley Tourism Board</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-primary/20 p-2 rounded-full mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lightText font-bold">Coverage Area</h3>
                      <p className="text-lightText/80">Allentown, Bethlehem, Easton and surrounding Lehigh Valley</p>
                    </div>
                  </div>
                </div>
                <a 
                  href={projects[0].externalUrl}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-6 rounded-md text-base transition-colors inline-flex items-center"
                >
                  View Commercial on Official Site
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center mb-12 gap-2">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === category.id
                  ? 'bg-primary text-darkBg'
                  : 'bg-darkCard text-lightText/70 hover:bg-darkCard/70'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredProjects.map(project => (
            <PortfolioItem 
              key={project.id} 
              project={project} 
              categories={categories}
              onClick={() => openLightbox(project)} 
            />
          ))}
        </motion.div>

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-lightText/60 text-xl">No projects found in this category.</p>
          </div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <h2 className="text-3xl font-bold text-lightText mb-6">Ready for Your Next Project?</h2>
          <p className="text-xl text-lightText/80 max-w-3xl mx-auto mb-8">
            Contact me to discuss how my drone cinematography and photography services can elevate your next project.
          </p>
          <a 
            href="/contact" 
            className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-8 rounded-md text-lg transition-colors inline-block"
          >
            Start a Project
          </a>
        </motion.div>

        {/* Lightbox */}
        {lightboxOpen && currentProject && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-black opacity-90" onClick={closeLightbox}></div>
              </div>

              <div className="inline-block w-full max-w-6xl overflow-hidden text-left align-middle transition-all transform bg-darkBg rounded-lg shadow-xl">
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 z-50 p-2 text-lightText hover:text-primary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="lg:flex">
                  <div className="lg:w-8/12 relative">
                    <div className="pt-[56.25%] relative">
                      {currentProject.isImage ? (
                        <div className="absolute inset-0 w-full h-full">
                          <WebsitePreview 
                            site={currentProject.title.includes("Lehigh") ? "discover-lehigh-valley" : "pa-gets-it-done"}
                            onClick={() => currentProject.externalUrl && window.open(currentProject.externalUrl, '_blank')}
                          />
                        </div>
                      ) : (
                        <video
                          className="absolute inset-0 w-full h-full object-cover"
                          src={currentProject.video}
                          controls
                          autoPlay
                          playsInline
                        ></video>
                      )}
                    </div>
                  </div>
                  <div className="p-8 lg:w-4/12">
                    <h3 className="text-2xl font-bold text-lightText mb-4">{currentProject.title}</h3>
                    <p className="text-lightText/80 mb-6">{currentProject.description}</p>
                    
                    <div className="mb-6">
                      <h4 className="text-sm uppercase text-lightText/60 mb-2">Client</h4>
                      <p className="text-lightText">{currentProject.client}</p>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-sm uppercase text-lightText/60 mb-2">Location</h4>
                      <p className="text-lightText">{currentProject.location}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm uppercase text-lightText/60 mb-2">Deliverables</h4>
                      <p className="text-lightText">{currentProject.deliverables}</p>
                    </div>
                    
                    {currentProject.isImage && currentProject.externalUrl && (
                      <a 
                        href={currentProject.externalUrl}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="mt-8 bg-primary hover:bg-primary/90 text-darkBg font-bold py-2 px-4 rounded-md text-sm transition-colors inline-flex items-center"
                      >
                        Visit Project Website
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Portfolio Item Component
const PortfolioItem = ({ project, categories, onClick }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="overflow-hidden rounded-lg bg-darkCard hover:bg-darkCard/80 transition-colors cursor-pointer group"
    onClick={onClick}
  >
    <div className="relative pt-[56.25%]">
      {project.isImage ? (
        <div className="absolute inset-0">
          <WebsitePreview 
            site={project.title.includes("Lehigh") ? "discover-lehigh-valley" : "pa-gets-it-done"}
            onClick={() => project.externalUrl && window.open(project.externalUrl, '_blank')}
          />
        </div>
      ) : (
        <video 
          src={project.video} 
          poster={project.thumbnail}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          autoPlay
          muted
          loop
          playsInline
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-darkBg/80 to-transparent opacity-70"></div>
      
      {project.isImage ? (
        <div className="absolute top-4 right-4 bg-primary/90 rounded-full p-2 text-darkBg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </div>
      ) : (
        <div className="absolute top-4 right-4 bg-primary/90 rounded-full p-2 text-darkBg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
    <div className="p-6">
      <div className="mb-2">
        <span className="inline-block px-3 py-1 text-xs font-semibold bg-primary/20 text-primary rounded-full">
          {categories.find(cat => cat.id === project.category)?.name || project.category}
        </span>
      </div>
      <h3 className="text-xl font-bold text-lightText mb-2">{project.title}</h3>
      <p className="text-lightText/60 text-sm mb-4">{project.client} • {project.location}</p>
      <div className="flex items-center text-lightText/70 group-hover:text-primary transition-colors">
        <span className="text-sm font-medium">View Details</span>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 transform group-hover:translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
    </div>
  </motion.div>
);

export default Portfolio; 